// Nursing documentation and fluid balance — the bedside half of the record.
//
// Optimised for fast entry rather than completeness: a nurse charting mid-round
// on a tablet needs three taps and a number, not a form. Both entry panels are
// inline and stay open after a save, because fluid balance in particular is
// charted several times in a row.
import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input, Textarea, NativeSelect } from '@/components/ui/Input';
import { LoadingState } from '@/components/ui/States';
import VoiceFormFiller, { type VoiceField } from '@/components/VoiceFormFiller';
import { ApiError } from '@/lib/api/client';
import { appendText, mergeTextFields, numField, selectField, textField, vNum, vOneOf, vText, type Extracted } from '@/lib/voiceFill';
import {
  useNursingNotes, useCreateNursingNote,
  useFluidBalance, useRecordFluid, useDeleteFluidEntry,
} from '../hooks';
import {
  FLUID_INPUT_CATEGORIES, FLUID_OUTPUT_CATEGORIES,
  type FluidDirection, type NursingNoteInput, type Shift,
} from '../types';
import { Section, Empty, Metric, fmtDateTime, fmtTime, useCan, BackdateField } from '../ui';

// ── Nursing notes ────────────────────────────────────────────────

const NOTE_TYPES = ['observation', 'assessment', 'intervention', 'handover', 'incident'] as const;

// One dictation covering the whole note, rather than a mic per field.
const NURSING_VOICE_FIELDS: VoiceField[] = [
  { key: 'observations', label: 'Observations', type: 'text' },
  { key: 'patient_response', label: 'Patient response', type: 'text' },
  { key: 'handover_notes', label: 'Handover notes', type: 'text' },
];

export function NursingNotesSection({ admissionId, readOnly }: { admissionId: number; readOnly: boolean }) {
  const can = useCan();
  const [shift, setShift] = useState<string>('');
  const { data, isLoading } = useNursingNotes(admissionId, shift || undefined);
  const create = useCreateNursingNote(admissionId);
  const [composing, setComposing] = useState(false);
  const [f, setF] = useState<NursingNoteInput>({ note_type: 'observation', shift: currentShift() });
  const [recordedAt, setRecordedAt] = useState<string | undefined>(undefined);
  const [err, setErr] = useState<string | null>(null);

  const mayWrite = can('ip:nursing-note') && !readOnly;
  const set = (k: keyof NursingNoteInput, v: unknown) => setF(p => ({ ...p, [k]: v }));

  return (
    <Section
      focusId="nursing_note"
      title="Nursing notes"
      subtitle="Bedside observations, interventions and handover — kept separate from physician notes."
      action={
        <>
          <NativeSelect className="h-7 w-auto text-xs" value={shift} onChange={e => setShift(e.target.value)}>
            <option value="">All shifts</option>
            {['morning', 'evening', 'night'].map(s => <option key={s} value={s}>{s}</option>)}
          </NativeSelect>
          {mayWrite && !composing && <Button size="sm" data-focus="nursing_note:new" onClick={() => setComposing(true)}>+ Nursing note</Button>}
        </>
      }
    >
      {composing && (
        <div className="mb-3 space-y-2 rounded-lg border border-border bg-muted/40 p-3">
          <div className="flex flex-wrap gap-2">
            <NativeSelect className="h-8 w-auto text-xs" value={f.note_type ?? 'observation'}
              onChange={e => set('note_type', e.target.value)}>
              {NOTE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </NativeSelect>
            <NativeSelect className="h-8 w-auto text-xs" value={f.shift ?? ''}
              onChange={e => set('shift', e.target.value as Shift)}>
              {['morning', 'evening', 'night'].map(s => <option key={s} value={s}>{s} shift</option>)}
            </NativeSelect>
          </div>
          <VoiceFormFiller
            fields={NURSING_VOICE_FIELDS}
            label="🎙 Dictate this note"
            hint="Patient comfortable, ambulating with support, tolerated diet; hand over: watch the IV site"
            onFields={extracted => setF(p => ({ ...p, ...mergeTextFields(p, extracted, ['observations', 'patient_response', 'handover_notes']) }))}
          />
          <Textarea rows={2} value={f.observations ?? ''} onChange={e => set('observations', e.target.value)}
            placeholder="Observations — how the patient is" />
          <Input value={f.patient_response ?? ''} onChange={e => set('patient_response', e.target.value)}
            placeholder="Patient response to care given" />
          <Input value={f.handover_notes ?? ''} onChange={e => set('handover_notes', e.target.value)}
            placeholder="Handover — what the next shift needs to know" />
          <BackdateField value={recordedAt} onChange={setRecordedAt} label="Recorded at" />
          {err && <div className="rounded bg-danger-soft px-3 py-2 text-[12px] text-danger-ink">{err}</div>}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => { setComposing(false); setErr(null); }}>Cancel</Button>
            <Button size="sm" loading={create.isPending} onClick={() => {
              setErr(null);
              create.mutate({ ...f, recorded_at: recordedAt }, {
                onSuccess: () => { setF({ note_type: 'observation', shift: f.shift }); setRecordedAt(undefined); },
                onError: e => setErr(e instanceof ApiError ? e.message : 'Could not save this note.'),
              });
            }}>Save note</Button>
          </div>
        </div>
      )}

      {isLoading ? <LoadingState label="Loading nursing notes…" />
        : !data?.length ? <Empty label="No nursing notes in this view" />
        : (
          <div className="space-y-2">
            {data.map(n => (
              <div key={n.id} data-focus={`nursing_note:${n.id}`} className="rounded-lg border border-border px-3 py-2">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[12.5px] font-semibold">{fmtDateTime(n.recorded_at)}</span>
                  <Badge variant="neutral">{n.note_type}</Badge>
                  {n.shift && <Badge variant="info">{n.shift}</Badge>}
                </div>
                {n.observations && <p className="mt-1 whitespace-pre-wrap text-[12.5px]">{n.observations}</p>}
                {n.patient_response && (
                  <p className="mt-1 text-[12px]"><span className="text-ink-muted">Response: </span>{n.patient_response}</p>
                )}
                {n.handover_notes && (
                  <p className="mt-1 text-[12px]"><span className="text-ink-muted">Handover: </span>{n.handover_notes}</p>
                )}
                <div className="mt-1.5 text-[11px] text-ink-muted">
                  {n.author_name || n.recorded_by || 'unknown'}
                </div>
              </div>
            ))}
          </div>
        )}
    </Section>
  );
}

function currentShift(): Shift {
  const h = new Date().getHours();
  if (h < 14) return 'morning';
  if (h < 21) return 'evening';
  return 'night';
}

// ── Fluid balance ────────────────────────────────────────────────

// Fluids are charted several times a shift, often with gloves on — one
// dictation ("urine output 350 ml via catheter", "IV normal saline 500 ml")
// fills direction, category, volume and detail together.
const FLUID_VOICE_FIELDS = [
  selectField('direction', 'Direction — input (given to the patient) or output (lost by the patient)', ['input', 'output']),
  selectField('category', 'Category — oral, iv (IV fluids), blood-product, enteral (tube feed); urine, drain, vomitus, stool, blood-loss; or other',
    [...new Set([...FLUID_INPUT_CATEGORIES, ...FLUID_OUTPUT_CATEGORIES])]),
  numField('quantity_ml', 'Volume in mL'),
  textField('route_detail', 'Detail — fluid name, route, site'),
];

export function FluidSection({ admissionId, readOnly }: { admissionId: number; readOnly: boolean }) {
  const can = useCan();
  const { data, isLoading } = useFluidBalance(admissionId);
  const record = useRecordFluid(admissionId);
  const remove = useDeleteFluidEntry(admissionId);

  const [direction, setDirection] = useState<FluidDirection>('input');
  const [category, setCategory] = useState<string>('oral');
  const [qty, setQty] = useState('');
  const [detail, setDetail] = useState('');
  const [recordedAt, setRecordedAt] = useState<string | undefined>(undefined);
  const [err, setErr] = useState<string | null>(null);

  const mayWrite = can('ip:fluid-balance') && !readOnly;
  const categories = direction === 'input' ? FLUID_INPUT_CATEGORIES : FLUID_OUTPUT_CATEGORIES;

  const switchDirection = (d: FluidDirection) => {
    setDirection(d);
    // Categories are pinned to their direction (the DB refuses "urine input"),
    // so switching has to reset the category rather than leave an invalid pair.
    setCategory(d === 'input' ? 'oral' : 'urine');
  };

  const applyVoice = (x: Extracted) => {
    const dir = vOneOf(x, 'direction', ['input', 'output'] as const);
    const cat = vText(x, 'category')?.toLowerCase();
    // Infer the direction from the category when only the category was said
    // ("urine 300") — the two lists overlap only on "other".
    const effectiveDir: FluidDirection = dir
      ?? (cat && (FLUID_OUTPUT_CATEGORIES as readonly string[]).includes(cat) && cat !== 'other' ? 'output'
        : cat && (FLUID_INPUT_CATEGORIES as readonly string[]).includes(cat) && cat !== 'other' ? 'input'
        : direction);
    const allowed: readonly string[] = effectiveDir === 'input' ? FLUID_INPUT_CATEGORIES : FLUID_OUTPUT_CATEGORIES;
    if (effectiveDir !== direction) switchDirection(effectiveDir);
    if (cat && allowed.includes(cat)) setCategory(cat);
    const n = vNum(x, 'quantity_ml');
    if (n !== undefined && n > 0) setQty(String(Math.round(n)));
    const d = vText(x, 'route_detail');
    if (d) setDetail(p => appendText(p, d, '; '));
  };

  const submit = () => {
    setErr(null);
    record.mutate(
      { direction, category, quantity_ml: Number(qty), route_detail: detail || undefined, recorded_datetime: recordedAt },
      {
        // Cleared for the next entry, but the panel stays open — fluids are
        // charted several times in a row, not once.
        onSuccess: () => { setQty(''); setDetail(''); setRecordedAt(undefined); },
        onError: e => setErr(e instanceof ApiError ? e.message : 'Could not record this entry.'),
      }
    );
  };

  const t = data?.totals;

  return (
    <Section focusId="fluid_balance" title="Fluid balance" subtitle="Totals are computed from the entries below, never stored.">
      {t && (
        <div className="mb-3 grid gap-3 sm:grid-cols-3">
          {([['Current window', t.window], ['Last 24 hours', t.last_24h], ['Cumulative', t.cumulative]] as const)
            .map(([label, v]) => (
              <div key={label} className="rounded-lg border border-border px-3 py-2">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-ink-muted">{label}</div>
                <div className="mt-1 flex gap-4">
                  <Metric label="In" value={`${v.input} mL`} />
                  <Metric label="Out" value={`${v.output} mL`} />
                  <Metric label="Net" value={`${v.net >= 0 ? '+' : ''}${v.net} mL`}
                    tone={Math.abs(v.net) > 2000 ? 'alert' : 'normal'} />
                </div>
              </div>
            ))}
        </div>
      )}

      {mayWrite && (
        <div className="mb-3 rounded-lg border border-border bg-muted/40 p-3">
        <VoiceFormFiller fields={FLUID_VOICE_FIELDS} onFields={applyVoice} label="🎙 Dictate entry"
          hint="IV normal saline 500 ml, right forearm" />
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <label className="mb-1 block text-xs font-semibold">Direction</label>
            <NativeSelect className="w-28" value={direction}
              onChange={e => switchDirection(e.target.value as FluidDirection)}>
              <option value="input">Input</option>
              <option value="output">Output</option>
            </NativeSelect>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold">Category</label>
            <NativeSelect className="w-36" value={category} onChange={e => setCategory(e.target.value)}>
              {categories.map(c => <option key={c} value={c}>{c}</option>)}
            </NativeSelect>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold">Volume (mL)</label>
            <Input className="w-28" value={qty} onChange={e => setQty(e.target.value)} inputMode="numeric" />
          </div>
          <div className="min-w-[180px] flex-1">
            <label className="mb-1 block text-xs font-semibold">Detail (optional)</label>
            <Input value={detail} onChange={e => setDetail(e.target.value)} placeholder="e.g. NS 0.9% via right forearm" />
          </div>
          <BackdateField value={recordedAt} onChange={setRecordedAt} label="Recorded at" />
          <Button size="sm" onClick={submit} loading={record.isPending} disabled={!qty}>Add</Button>
        </div>
        </div>
      )}

      {err && <div className="mb-2 rounded bg-danger-soft px-3 py-2 text-[12px] text-danger-ink">{err}</div>}

      {isLoading ? <LoadingState label="Loading fluid chart…" />
        : !data?.entries.length ? <Empty label="No fluid balance charted in this window" />
        : (
          <div className="-mx-1 overflow-x-auto px-1">
            <table className="w-full min-w-[520px] text-[12.5px]">
              <thead>
                <tr className="border-b border-border text-left text-[10.5px] uppercase tracking-wide text-ink-muted">
                  <th className="py-1.5 pr-3 font-semibold">Time</th>
                  <th className="py-1.5 pr-3 font-semibold">Direction</th>
                  <th className="py-1.5 pr-3 font-semibold">Category</th>
                  <th className="py-1.5 pr-3 font-semibold">Volume</th>
                  <th className="py-1.5 pr-3 font-semibold">Detail</th>
                  <th className="py-1.5 pr-3 font-semibold">By</th>
                  {mayWrite && <th className="py-1.5" />}
                </tr>
              </thead>
              <tbody>
                {data.entries.map(e => (
                  <tr key={e.id} data-focus={`fluid_balance:${e.id}`} className="border-b border-border/60 last:border-0">
                    <td className="py-1.5 pr-3 whitespace-nowrap tabular-nums">{fmtTime(e.recorded_datetime)}</td>
                    <td className="py-1.5 pr-3">
                      <Badge variant={e.direction === 'input' ? 'info' : 'neutral'}>{e.direction}</Badge>
                    </td>
                    <td className="py-1.5 pr-3">{e.category}</td>
                    <td className="py-1.5 pr-3 tabular-nums">{Number(e.quantity_ml)} mL</td>
                    <td className="py-1.5 pr-3 text-ink-muted">{e.route_detail || '—'}</td>
                    <td className="py-1.5 pr-3 text-ink-muted">{e.recorded_by || '—'}</td>
                    {mayWrite && (
                      <td className="py-1.5">
                        <Button variant="link" size="sm" onClick={() => remove.mutate(e.id)}>Remove</Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </Section>
  );
}
